import React, { useState } from 'react';
import ShellPage from '../layout/ShellPage';
import RfqListToolbar, { RfqListView, presetOf } from '../components/rfqs/RfqListToolbar';
import RfqCardGrid from '../components/rfqs/RfqCardGrid';
import RfqListTable from '../components/rfqs/RfqListTable';
import RobbieNote from '../components/rfqs/RfqListStates';
import { useIsMobile } from '../hooks/useIsMobile';
import { useRfqList, DEFAULT_SORT } from '../hooks/useRfqList';
import { PAGE_LIMIT } from '../hooks/dashboardMappers';

/**
 * /rfqs — the complete org RFQ list the dashboard deliberately isn't:
 * every status (active, awarded, cancelled), in a card grid or a table,
 * with search / status / stage / ownership filters and sorting. Opening
 * an RFQ continues in the wizard at its current step.
 */
interface RFQListProps {
    onCreateRFQ: () => void;
    onViewRFQ: (rfqId: string) => void;
}

const RFQList: React.FC<RFQListProps> = ({ onCreateRFQ, onViewRFQ }) => {
    const list = useRfqList();
    const [view, setView] = useState<RfqListView>('cards');
    // A phone gets cards whatever was chosen on a desktop: the table's five
    // columns do not survive 375px, and the toolbar hides the switch to
    // match. The choice itself is kept, so rotating back restores it.
    const isMobile = useIsMobile();
    const effectiveView: RfqListView = isMobile ? 'cards' : view;

    // Card view sorts through fixed presets; a column sort that has no preset
    // (e.g. parts) falls back to the default order rather than lying in the
    // select.
    const pickView = (next: RfqListView) => {
        if (next === 'cards' && !presetOf(list.sort)) list.setSort(DEFAULT_SORT);
        setView(next);
    };

    const empty = list.hasAnyRfqs ? (
        <RobbieNote
            message="I couldn't find an RFQ matching those filters — clear them and the whole workspace comes back."
            action={list.filtersApplied ? { label: 'Clear filters', onClick: list.clearFilters } : undefined}
        />
    ) : (
        <RobbieNote
            message="There are no RFQs in this workspace yet — start one and I'll take it from BOM upload to award."
            action={{ label: 'New RFQ', onClick: onCreateRFQ }}
        />
    );

    return (
        <ShellPage
            title="RFQs"
            activeId="rfqs"
            rfqCount={list.loading ? undefined : list.activeCount}
            onCreateRFQ={onCreateRFQ}
        >
            <div className="flex flex-col gap-[16px] pb-12">
                {list.error ? (
                    <RobbieNote
                        message="I couldn't load the workspace's RFQs — nothing came back from the server. Try again and I'll refresh the list."
                        action={{ label: 'Try again', onClick: list.refresh }}
                    />
                ) : (
                    <>
                        <RfqListToolbar
                            search={list.search}
                            onSearch={list.setSearch}
                            status={list.status}
                            onStatus={list.setStatus}
                            statusCounts={list.statusCounts}
                            stage={list.stage}
                            onStage={list.setStage}
                            owner={list.owner}
                            onOwner={list.setOwner}
                            sort={list.sort}
                            onSort={list.setSort}
                            view={view}
                            onView={pickView}
                        />
                        {list.truncated && (
                            <p className="text-meta text-ink-muted">
                                I'm showing the {PAGE_LIMIT} most recently created of {list.orgTotal} RFQs —
                                search and filters apply to these.
                            </p>
                        )}
                        {effectiveView === 'cards' ? (
                            <RfqCardGrid rows={list.rows} loading={list.loading} onOpen={onViewRFQ} empty={empty} />
                        ) : (
                            <RfqListTable
                                rows={list.rows}
                                sort={list.sort}
                                onSort={list.setSort}
                                loading={list.loading}
                                onOpen={onViewRFQ}
                                empty={empty}
                            />
                        )}
                    </>
                )}
            </div>
        </ShellPage>
    );
};

export default RFQList;
